import React from 'react';
import { Box, Typography } from '@mui/material';
import { COLORS } from '../../pages/app/reports/groups-teams-activity/constants';
import { GroupRow } from '../../pages/app/reports/groups-teams-activity/types';
import { StatusDot } from './status-dot';
import { ProgressBar } from './progress-bar';
import { TrendIndicator } from './trend-indicator';

interface GroupsTableProps {
    groups: GroupRow[];
    onGroupClick: (group: GroupRow) => void;
}

const thStyle = {
    fontSize: '10px',
    color: COLORS.grayMedium,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '.5px',
    padding: '8px 12px',
    textAlign: 'left' as const,
    borderBottom: `2px solid ${COLORS.border}`,
    whiteSpace: 'nowrap' as const,
};

const tdStyle = {
    padding: '10px 12px',
    borderBottom: `1px solid ${COLORS.borderLight}`,
    verticalAlign: 'middle' as const,
    fontSize: '13px',
};

export const GroupsTable: React.FC<GroupsTableProps> = ({ groups, onGroupClick }) => {
    // Separate default rows and sort them to bottom
    const regularGroups = groups.filter((g) => !g.isDefault);
    const defaultGroups = groups.filter((g) => g.isDefault);
    const sortedGroups = [...regularGroups, ...defaultGroups];

    return (
        <Box
            sx={{
                backgroundColor: COLORS.cardBg,
                borderRadius: '10px',
                border: `1px solid ${COLORS.border}`,
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    padding: '14px 18px',
                    borderBottom: `1px solid ${COLORS.borderLight}`,
                }}
            >
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>Groups</Typography>
                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: COLORS.grayDark }}>
                    Click a group to see its teams
                </Typography>
            </Box>

            {/* Table */}
            <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ ...thStyle, width: '220px' }}>Group</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Teams</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Users</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Active</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Inactive</th>
                            <th style={{ ...thStyle, width: '180px' }}>% Active</th>
                            <th style={{ ...thStyle, textAlign: 'center' }}>Trend</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedGroups.map((group) => {
                            const isDefault = group.isDefault;
                            return (
                                <tr
                                    key={group.id}
                                    onClick={() => onGroupClick(group)}
                                    style={{
                                        cursor: 'pointer',
                                        borderTop: isDefault ? `2px solid ${COLORS.border}` : undefined,
                                    }}
                                    onMouseEnter={(e) => {
                                        (e.currentTarget as HTMLElement).style.backgroundColor = '#F8FAFC';
                                    }}
                                    onMouseLeave={(e) => {
                                        (e.currentTarget as HTMLElement).style.backgroundColor = '';
                                    }}
                                >
                                    <td style={tdStyle}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <StatusDot percent={group.activePercent} isDefault={isDefault} />
                                            <Typography
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '13px',
                                                    color: isDefault ? COLORS.grayMedium : COLORS.textPrimary,
                                                    fontStyle: isDefault ? 'italic' : 'normal',
                                                }}
                                            >
                                                {group.name}
                                            </Typography>
                                        </Box>
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', color: isDefault ? COLORS.grayMedium : undefined }}>
                                        {group.teamCount}
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', color: isDefault ? COLORS.grayMedium : undefined }}>
                                        {group.userCount.toLocaleString()}
                                    </td>
                                    <td
                                        style={{
                                            ...tdStyle,
                                            textAlign: 'right',
                                            color: isDefault ? COLORS.grayMedium : COLORS.primary,
                                            fontWeight: isDefault ? 400 : 600,
                                        }}
                                    >
                                        {group.activeCount}
                                    </td>
                                    <td
                                        style={{
                                            ...tdStyle,
                                            textAlign: 'right',
                                            color: isDefault ? COLORS.grayMedium : COLORS.red,
                                        }}
                                    >
                                        {group.inactiveCount.toLocaleString()}
                                    </td>
                                    <td style={tdStyle}>
                                        <ProgressBar percent={group.activePercent} isDefault={isDefault} />
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                        <TrendIndicator direction={group.trend} value={group.trendValue} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </Box>
        </Box>
    );
};
